import type { FC, ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import type { AgentMode } from '@/index'
import { BaseDialog } from './BaseDialog'

interface SessionDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  readonly breadcrumb: string
  readonly children: ReactNode
  readonly agentMode?: AgentMode | null | undefined
  readonly agentModeBadge?: ReactNode
  readonly agentModeSelectable?: boolean
  readonly onAgentModeChange?: ((mode: AgentMode | null) => void) | undefined
  readonly dangerouslySkipPermissions?: boolean | undefined
  readonly autoModeSupported?: boolean | undefined
  readonly windowControlSlot1?: ReactNode
  readonly windowControlSlot2?: ReactNode
  readonly windowControlSlot3?: ReactNode
  readonly controlsRowLeft?: ReactNode
  readonly controlsRowCenter?: ReactNode
  readonly controlsRowRight?: ReactNode
  readonly statusRowLeft?: ReactNode
  readonly statusRowCenter?: ReactNode
  readonly statusRowRight?: ReactNode
  readonly isExpanded?: boolean
  readonly fontSize?: string
  readonly dialogWidth?: string
  readonly dialogHeight?: string
}

interface ControlsRowProps {
  readonly controlsRowLeft?: ReactNode
  readonly controlsRowCenter?: ReactNode
  readonly controlsRowRight?: ReactNode
}

interface StatusRowProps {
  readonly statusRowLeft?: ReactNode
  readonly statusRowCenter?: ReactNode
  readonly statusRowRight?: ReactNode
}

export const SessionDialog: FC<SessionDialogProps> = ({
  open,
  onClose,
  title,
  breadcrumb,
  children,
  agentMode,
  agentModeBadge,
  agentModeSelectable = false,
  onAgentModeChange,
  dangerouslySkipPermissions,
  autoModeSupported,
  windowControlSlot1,
  windowControlSlot2,
  windowControlSlot3,
  controlsRowLeft,
  controlsRowCenter,
  controlsRowRight,
  statusRowLeft,
  statusRowCenter,
  statusRowRight,
  isExpanded,
  fontSize,
  dialogWidth,
  dialogHeight,
}) => {
  const hasControlsRow = !!(controlsRowLeft || controlsRowCenter || controlsRowRight)
  const hasStatusRow = !!(statusRowLeft || statusRowCenter || statusRowRight)

  const controlsRow = hasControlsRow ? (
    <ControlsRow
      controlsRowLeft={controlsRowLeft}
      controlsRowCenter={controlsRowCenter}
      controlsRowRight={controlsRowRight}
    />
  ) : undefined

  const statusRow = hasStatusRow ? (
    <StatusRow
      statusRowLeft={statusRowLeft}
      statusRowCenter={statusRowCenter}
      statusRowRight={statusRowRight}
    />
  ) : undefined

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={title}
      breadcrumb={breadcrumb}
      agentMode={agentMode}
      agentModeBadge={agentModeBadge}
      agentModeSelectable={agentModeSelectable}
      onAgentModeChange={onAgentModeChange}
      dangerouslySkipPermissions={dangerouslySkipPermissions}
      autoModeSupported={autoModeSupported}
      windowControlSlot1={windowControlSlot1}
      windowControlSlot2={windowControlSlot2}
      windowControlSlot3={windowControlSlot3}
      controlsRow={controlsRow}
      statusRow={statusRow}
      isExpanded={isExpanded}
      fontSize={fontSize}
      {...(dialogWidth !== undefined && { dialogWidth })}
      {...(dialogHeight !== undefined && { dialogHeight })}
    >
      {children}
    </BaseDialog>
  )
}

const ControlsRow: FC<ControlsRowProps> = ({
  controlsRowLeft,
  controlsRowCenter,
  controlsRowRight,
}) => (
  <Box sx={controlsRowContainerSx}>
    <Box sx={controlsRowLeftSx}>{controlsRowLeft}</Box>
    <Box sx={controlsRowCenterSx}>{controlsRowCenter}</Box>
    <Box sx={controlsRowRightSx}>{controlsRowRight}</Box>
  </Box>
)

const StatusRow: FC<StatusRowProps> = ({ statusRowLeft, statusRowCenter, statusRowRight }) => (
  <Box sx={statusRowContainerSx}>
    <Box sx={statusRowLeftSx}>{statusRowLeft}</Box>
    <Box sx={statusRowCenterSx}>{statusRowCenter}</Box>
    <Box sx={statusRowRightSx}>{statusRowRight}</Box>
  </Box>
)

const controlsRowContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
}

const controlsRowLeftSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const controlsRowCenterSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
}

const controlsRowRightSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const statusRowContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
}

const statusRowLeftSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const statusRowCenterSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
}

const statusRowRightSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}
